import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff } from 'lucide-react';

export default function RegisterPage() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const result = await register(email, password, name);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-12">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#525252] mb-4">
            Join the Gallery
          </p>
          <h1 className="font-serif text-4xl text-[#F5F5F0] tracking-tight">
            Create Account
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-[#D84B79]/10 border border-[#D84B79]/30 text-[#D84B79] text-sm">
              {error}
            </div>
          )}

          <div>
            <label 
              htmlFor="name" 
              className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] block mb-3"
            >
              Name
            </label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-[#F5F5F0] focus:border-[#C5A059] focus-visible:ring-0"
              placeholder="Your name"
              data-testid="register-name"
            />
          </div>

          <div>
            <label 
              htmlFor="email" 
              className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] block mb-3"
            >
              Email
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-[#F5F5F0] focus:border-[#C5A059] focus-visible:ring-0"
              placeholder="your@email.com"
              data-testid="register-email"
            />
          </div>

          <div>
            <label 
              htmlFor="password" 
              className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] block mb-3"
            >
              Password
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 pr-10 text-[#F5F5F0] focus:border-[#C5A059] focus-visible:ring-0"
                placeholder="••••••••"
                data-testid="register-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-0 top-1/2 -translate-y-1/2 text-[#525252] hover:text-[#F5F5F0] transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="text-[#525252] text-xs mt-2">
              Minimum 6 characters
            </p>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#C5A059] text-[#0A0A0A] hover:bg-[#E0BE77] font-mono text-xs uppercase tracking-[0.2em] py-6 mt-8"
            data-testid="register-submit"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>

        <p className="text-center text-[#A3A3A3] text-sm mt-8">
          Already have an account?{' '}
          <Link 
            to="/login" 
            className="text-[#C5A059] hover:text-[#E0BE77] transition-colors"
            data-testid="register-login-link"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
